import { useCallback, useState } from 'react'
import ReactDOM from 'react-dom'

import Button from '../../../common/Button'

const PAYMENT_TITLE = 'Retry payment?'
const PAYMENT_MESSAGE =
    'The lien was already sent to the county clerk, but payment is still pending. Retry payment to complete the filing?'

const RESUBMIT_REJECTED_TITLE = 'Retry lien filing?'
const RESUBMIT_REJECTED_MESSAGE =
    'The county clerk rejected this lien. Retry will submit a new filing. Continue?'

const RESUBMIT_ERROR_TITLE = 'Retry lien filing?'
const RESUBMIT_ERROR_DEFAULT_MESSAGE =
    'This lien has an error. Retry will attempt the filing again. Continue?'

const REGENERATE_MANUAL_TITLE = 'Regenerate lien filing?'
const REGENERATE_MANUAL_MESSAGE =
    'This will rebuild the lien file for manual county recording and save a new copy in the Manual Liens folder. Continue?'

const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
}

const cardStyle = {
    backgroundColor: 'var(--background, var(--white, #fff))',
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: 320,
    maxWidth: 'min(420px, 92vw)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
}

const titleStyle = { margin: '0 0 8px', fontSize: 16, fontWeight: 600 }
const textStyle = { margin: '0 0 16px', fontSize: 14, lineHeight: 1.45, color: 'var(--subtitle, #555)', whiteSpace: 'pre-line' }
const actionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }

export function getLienRetryConfirmCopy({ actionType, sideIsRejected, errorMessage }) {
    if (actionType === 'payment') {
        return { title: PAYMENT_TITLE, message: PAYMENT_MESSAGE }
    }

    if (actionType === 'regenerate') {
        return { title: REGENERATE_MANUAL_TITLE, message: REGENERATE_MANUAL_MESSAGE }
    }

    if (sideIsRejected) {
        return { title: RESUBMIT_REJECTED_TITLE, message: RESUBMIT_REJECTED_MESSAGE }
    }

    const trimmedError = `${errorMessage || ''}`.trim()
    const message = trimmedError
        ? `${trimmedError}\n\nRetry will attempt the filing again. Continue?`
        : RESUBMIT_ERROR_DEFAULT_MESSAGE

    return { title: RESUBMIT_ERROR_TITLE, message }
}

export function LienRetryConfirmModal({ actionType, sideIsRejected, errorMessage, onConfirm, onCancel }) {
    const overlayRoot = typeof document !== 'undefined' ? document.getElementById('overlay-root') : null
    if (!overlayRoot) {
        return null
    }

    const { title, message } = getLienRetryConfirmCopy({ actionType, sideIsRejected, errorMessage })

    return ReactDOM.createPortal(
        <div style={overlayStyle} role="presentation" onClick={onCancel}>
            <div
                style={cardStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="lien-retry-confirm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 id="lien-retry-confirm-title" style={titleStyle}>{title}</h3>
                <p style={textStyle}>{message}</p>
                <div style={actionsStyle}>
                    <Button click={onCancel} btnStyle="btn_white">Cancel</Button>
                    <Button click={onConfirm}>Confirm</Button>
                </div>
            </div>
        </div>,
        overlayRoot,
    )
}

/**
 * React hook that renders the lien retry confirmation modal and exposes an
 * imperative `confirm({ actionType, sideIsRejected, errorMessage })` function
 * returning a Promise<boolean>.
 */
export function useLienRetryConfirm() {
    const [pending, setPending] = useState(null)

    const confirm = useCallback(({ actionType, sideIsRejected, errorMessage }) => {
        return new Promise((resolve) => {
            let normalizedAction = 'resubmit'
            if (actionType === 'payment') {
                normalizedAction = 'payment'
            } else if (actionType === 'regenerate') {
                normalizedAction = 'regenerate'
            }

            setPending({
                actionType: normalizedAction,
                sideIsRejected: Boolean(sideIsRejected),
                errorMessage: errorMessage ?? '',
                resolve,
            })
        })
    }, [])

    const finish = useCallback((result) => {
        setPending((current) => {
            if (current && typeof current.resolve === 'function') {
                current.resolve(result)
            }
            return null
        })
    }, [])

    const modal = pending ? (
        <LienRetryConfirmModal
            actionType={pending.actionType}
            sideIsRejected={pending.sideIsRejected}
            errorMessage={pending.errorMessage}
            onConfirm={() => finish(true)}
            onCancel={() => finish(false)}
        />
    ) : null

    return { confirm, modal }
}

